import React, { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { WorkCharge, WorkshopSpecialty } from "../../types/garage/atelier";
import { JOB_STATUSES, useAtelierStore } from "@store/atelierStore";
import OrdreInterventionsHint, { OrdreNumHint, OrdrePlateHint } from "@components/ui/OrdreInterventionsHint";
import OrdreStatusHint from "@components/ui/OrdreStatusHint";
import { formatDateHour } from "@utils/timeGrid";
import { getOrdreById } from "@store/ordreStore";
import { useDataStore } from "@store/user_org";
import type { OrdreStatus } from "types/ordre";
import { getStatusColor } from "@components/ui/dropdown/OrdreStatusDropdown";

/** Statut de l'ordre porté par la charge (servi par l'API). Le store des ordres
 *  n'est qu'un secours : il ne contient que ce que l'écran « Ordres » a chargé. */
export function chargeOrdreStatus(charge: WorkCharge): OrdreStatus | undefined {
  return (charge.ordreStatus as OrdreStatus | undefined)
    || (charge.ordre_id ? getOrdreById(charge.ordre_id)?.status : undefined);
}

const SPECIALTY_LABELS: Record<WorkshopSpecialty, string> = {
  mecanique: "Mécanique",
  carrosserie: "Carrosserie",
  peinture: "Peinture",
  diagnostique: "Diagnostique",
  service_rapide: "Service rapide",
  campagnes: "Campagnes",
  autre: "Autre",
};

const SPECIALTY_COLORS: Record<WorkshopSpecialty, { bg: string; border: string; color: string }> = {
  mecanique: { bg: "#EEF0FF", border: "#A5B4FC", color: "#4F46E5" },
  carrosserie: { bg: "#F3EEFF", border: "#C4A8FF", color: "#7C3AED" },
  peinture: { bg: "#FEE2E2", border: "#FECACA", color: "#DC2626" },
  diagnostique: { bg: "#D1FAE5", border: "#A7F3D0", color: "#059669" },
  service_rapide: { bg: "#ECE9FF", border: "#C5BFFF", color: "#6F61FF" },
  campagnes: { bg: "#FFEDD5", border: "#FED7AA", color: "#EA580C" },
  autre: { bg: "#F5F5F5", border: "#D9D9D9", color: "#737373" },
};

// --- Status Legend ---

export function StatusLegend() {
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <span
        className="text-h4 font-medium"
        style={{ fontFamily: "Poppins", color: "#A1A1A1" }}
      >
        Statuts
      </span>
      {JOB_STATUSES.map((s) => (
        <div
          key={s.id}
          className="flex flex-row items-center"
          style={{ gap: 8 }}
        >
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: 3,
              background: s.bgColor,
              border: `1px solid ${s.borderColor}`,
            }}
          />
          <span
            className="text-h4 font-medium"
            style={{ fontFamily: "Poppins", color: "#737373" }}
          >
            {s.label}
          </span>
        </div>
      ))}
    </div>
  );
}

// --- Charge Card ---

export function ChargeCard({
  charge,
  onSplitClick,
  onDragStart,
  isMergeTarget,
}: {
  charge: WorkCharge;
  onSplitClick: (c: WorkCharge) => void;
  onDragStart: (e: React.PointerEvent, c: WorkCharge) => void;
  isMergeTarget?: boolean;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const isDragging = useRef(false);
  const setHoveredChargeId = useAtelierStore((s) => s.setHoveredChargeId);
  // Same dark hover panel as atelier jobs, anchored to the right of the card.
  const [tip, setTip] = useState<{ top: number; left: number } | null>(null);

  const cw = charge.carWindow;
  // Délogé de MyO : sans objet une fois que Carvis fait foi (plus rien n'est écrit dans MyO).
  const carvisMaster = useDataStore((s) => !!s.orgData?.servicebox?.carvis_master);
  const evicted = charge.evicted && !carvisMaster;

  const sc = SPECIALTY_COLORS[charge.specialty] ?? SPECIALTY_COLORS.autre;
  // Statut de l'ordre : même bande colorée à gauche que les événements du planning
  // réception, pour lire l'avancement sans ouvrir la fiche. La couleur du métier
  // reste sur le fond et le reste du contour.
  const ordreStatus = chargeOrdreStatus(charge);

  return (
    <div
      ref={cardRef}
      data-charge-id={charge.id}
      data-charge-ordre={charge.ordre_id ?? ""}
      className="flex flex-row items-center justify-between shadow-sm"
      style={{
        background: isMergeTarget ? "#C7D2FE" : sc.bg,
        border: isMergeTarget
          ? "2px solid #6366F1"
          : evicted
            ? "2px solid #DC2626"
            : charge.autoGenerated
            ? `2px dashed ${sc.border}`
            : `1px solid ${sc.border}`,
        ...(ordreStatus ? { borderLeft: `5px solid ${getStatusColor(ordreStatus)}` } : null),
        borderRadius: 6,
        padding: "6px 8px",
        cursor: "grab",
        userSelect: "none",
        touchAction: "none",
        opacity: charge.autoGenerated ? 0.85 : 1,
        transform: isMergeTarget ? "scale(1.03)" : undefined,
        transition: "all 0.15s",
      }}
      onMouseEnter={() => {
        setHoveredChargeId(charge.id);
        const r = cardRef.current?.getBoundingClientRect();
        // Remonté quand la carte est trop basse : le panneau sortait de l'écran
        // pour les charges en bas de la pile.
        if (r) setTip({ top: Math.max(8, Math.min(r.top, window.innerHeight - 280)), left: r.right + 8 });
      }}
      onMouseLeave={() => {
        setHoveredChargeId(null);
        setTip(null);
      }}
      onPointerDown={(e) => {
        pointerStartRef.current = { x: e.clientX, y: e.clientY };
        isDragging.current = false;
      }}
      onPointerMove={(e) => {
        if (!pointerStartRef.current) return;
        if (
          Math.abs(e.clientX - pointerStartRef.current.x) > 4 ||
          Math.abs(e.clientY - pointerStartRef.current.y) > 4
        ) {
          if (!isDragging.current) {
            isDragging.current = true;
            // Capture pointer on the card div itself (not e.target which may be a child span)
            if (cardRef.current) {
              cardRef.current.setPointerCapture(e.pointerId);
            }
            onDragStart(e, charge);
          }
        }
      }}
      onPointerUp={(e) => {
        if (isDragging.current && cardRef.current) {
          cardRef.current.releasePointerCapture(e.pointerId);
        }
        if (!isDragging.current && pointerStartRef.current)
          onSplitClick(charge);
        pointerStartRef.current = null;
        isDragging.current = false;
      }}
    >
      <span
        className="text-h4 font-medium"
        style={{
          fontFamily: "Poppins",
          color: sc.color,
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {charge.clientName}
      </span>
      {evicted && (
        <span
          title="Délogé de MyO pour faire la place à un autre travail : à replacer"
          style={{
            fontSize: 9,
            fontWeight: 600,
            color: "#FFF",
            background: "#DC2626",
            borderRadius: 3,
            padding: "0 3px",
            lineHeight: "14px",
            flexShrink: 0,
            marginLeft: 4,
          }}
        >
          À replacer
        </span>
      )}
      {charge.autoGenerated && (
        <span
          style={{
            fontSize: 9,
            fontWeight: 600,
            color: "#FFF",
            background: "#6366F1",
            borderRadius: 3,
            padding: "0 3px",
            lineHeight: "14px",
            flexShrink: 0,
            marginLeft: 4,
          }}
        >
          Auto
        </span>
      )}
      <span
        className="text-h4 font-semibold"
        style={{ fontFamily: "Poppins", color: sc.color, opacity: 0.6, marginLeft: 4 }}
      >
        ({+(charge.hours || 0).toFixed(2)}h)
      </span>

      {/* Rendu dans un portail : dans le flux de la barre latérale, le panneau
          passait sous les colonnes de la grille (contexte d'empilement parent) et
          le planning se dessinait par-dessus son texte. */}
      {tip && !isDragging.current && createPortal(
        <div
          className="fixed bg-neutral-800 text-white rounded-lg px-3 py-2 pointer-events-none"
          style={{ top: tip.top, left: tip.left, maxWidth: 320, minWidth: 140, zIndex: 10001 }}
        >
          {/* Même bulle que les magnets planifiés (WorkerTimeGrid) : l'atelier
              lit les mêmes infos, heure de réception comprise, qu'un chantier
              soit déjà sur la grille ou encore à planifier. */}
          <div className="text-h4 font-medium">{charge.clientName}<OrdreNumHint ordreId={charge.ordre_id} /></div>
          {charge.vehicleName && <div className="text-h5 text-neutral-200">{charge.vehicleName}</div>}
          <OrdrePlateHint plate={charge.plate} ordreId={charge.ordre_id} />
          <div className="text-h5 text-neutral-300">
            {SPECIALTY_LABELS[charge.specialty] ?? "Autre"} · {+(charge.hours || 0).toFixed(2)}h ·{" "}
            {charge.status === "en_pause" ? "en pause" : "à répartir"}
          </div>
          {cw && (
            <div className="text-h5 text-neutral-400 mt-1">
              Réception : {formatDateHour(cw.depositDate, cw.depositStartHour)}
              {" — "}
              Restitution : {formatDateHour(cw.restitutionDate, cw.restitutionStartHour)}
            </div>
          )}
          <OrdreInterventionsHint ordreId={charge.ordre_id} />
          <OrdreStatusHint status={ordreStatus} />
          {charge.autoGenerated && (
            <div className="text-h5 text-indigo-300 mt-1">Généré automatiquement</div>
          )}
          {evicted && (
            <div className="text-h5 text-red-300 mt-1">Délogé de MyO pour faire la place à un autre travail : à replacer</div>
          )}
          <div className="text-h5 text-neutral-400 mt-1">Cliquer pour répartir</div>
        </div>,
        document.body,
      )}
    </div>
  );
}

// --- Charge Section ---

/** Clé de tri chronologique : réception (jour + heure) de la voiture. */
function receptionSortKey(c: WorkCharge): number {
  const day = c.carWindow?.depositDate?.slice(0, 10);
  if (!day) return Number.POSITIVE_INFINITY;
  const t = Date.parse(`${day}T00:00:00Z`);
  if (Number.isNaN(t)) return Number.POSITIVE_INFINITY;
  return t + (c.carWindow?.depositStartHour ?? 0) * 3_600_000;
}

/** Sous-dossiers de « Charges à répartir », dans l'ordre où l'atelier les traite :
 *  le devis initial d'abord (rien ne part sans lui), puis ce qui est prêt à être
 *  lancé, puis les devis supplémentaires, et le reste à la fin. */
const REPARTIR_GROUPS: { key: string; label: string; statuses: string[] }[] = [
  { key: "devis_initial", label: "Devis initial", statuses: ["devis_initial"] },
  { key: "pret", label: "Prêt", statuses: ["pret"] },
  { key: "devis_supp", label: "Devis supp.", statuses: ["devis_supp"] },
  { key: "autres", label: "Autres", statuses: [] },  // tout ce qui n'entre pas au-dessus
];

function ChargeGroup({
  label, charges, open, onToggle, onSplitClick, onChargeDragStart, mergeTargetChargeId,
}: {
  label: string;
  charges: WorkCharge[];
  open: boolean;
  onToggle: () => void;
  onSplitClick: (c: WorkCharge) => void;
  onChargeDragStart: (e: React.PointerEvent, c: WorkCharge) => void;
  mergeTargetChargeId?: string | null;
}) {
  const hours = charges.reduce((sum, c) => sum + (c.hours || 0), 0);
  return (
    <div className="flex flex-col" style={{ gap: 6 }}>
      <button
        type="button"
        onClick={onToggle}
        className="flex flex-row items-center justify-between"
        style={{
          background: "#F5F5F5", border: "1px solid #E5E5E5", borderRadius: 6,
          padding: "4px 8px", cursor: "pointer", width: "100%",
        }}
      >
        <span className="flex flex-row items-center" style={{ gap: 6 }}>
          <svg
            width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="#737373"
            strokeWidth={3} strokeLinecap="round" strokeLinejoin="round"
            style={{ transform: open ? "rotate(90deg)" : undefined, transition: "transform 0.15s" }}
          >
            <path d="M9 6l6 6-6 6" />
          </svg>
          <span className="text-h4 font-medium" style={{ fontFamily: "Poppins", color: "#525252" }}>
            {label}
          </span>
        </span>
        <span className="text-h5 font-medium" style={{ fontFamily: "Poppins", color: "#A1A1A1" }}>
          {charges.length} · {Math.round(hours * 10) / 10}h
        </span>
      </button>
      {open && charges.map((c) => (
        <ChargeCard
          key={c.id}
          charge={c}
          onSplitClick={onSplitClick}
          onDragStart={onChargeDragStart}
          isMergeTarget={mergeTargetChargeId === c.id}
        />
      ))}
    </div>
  );
}

export function ChargeSection({
  title,
  status,
  charges,
  onSplitClick,
  onChargeDragStart,
  isDropTarget,
  mergeTargetChargeId,
  groupByStatus,
}: {
  title: string;
  status: WorkCharge["status"];
  charges: WorkCharge[];
  onSplitClick: (c: WorkCharge) => void;
  onChargeDragStart: (e: React.PointerEvent, c: WorkCharge) => void;
  isDropTarget?: boolean;
  mergeTargetChargeId?: string | null;
  /** Découpe la section en sous-dossiers repliables par statut d'ordre. */
  groupByStatus?: boolean;
}) {
  // Ordre chronologique de réception (date puis heure) : l'atelier attaque les
  // voitures dans l'ordre où elles arrivent. Sans réception connue → en fin de
  // pile ; à égalité, ordre alphabétique.
  const sorted = useMemo(
    () =>
      [...charges].sort((a, b) => {
        const ka = receptionSortKey(a);
        const kb = receptionSortKey(b);
        return ka !== kb ? ka - kb : a.clientName.localeCompare(b.clientName);
      }),
    [charges],
  );

  // Sous-dossiers par statut d'ordre. Les groupes vides ne sont pas affichés.
  const groups = useMemo(() => {
    if (!groupByStatus) return [];
    const claimed = new Set(REPARTIR_GROUPS.flatMap((g) => g.statuses));
    return REPARTIR_GROUPS.map((g) => ({
      ...g,
      charges: sorted.filter((c) => {
        const st = chargeOrdreStatus(c);
        return g.statuses.length ? g.statuses.includes(st || "") : !st || !claimed.has(st);
      }),
    })).filter((g) => g.charges.length > 0);
  }, [groupByStatus, sorted]);

  // Le premier sous-dossier non vide est ouvert d'entrée ; l'état devient
  // explicite dès que quelqu'un replie ou déplie quelque chose.
  const [openKeys, setOpenKeys] = useState<string[] | null>(null);
  const firstKey = groups[0]?.key;
  const isOpen = (key: string) => (openKeys ? openKeys.includes(key) : key === firstKey);
  const toggle = (key: string) =>
    setOpenKeys((prev) => {
      const base = prev ?? (firstKey ? [firstKey] : []);
      return base.includes(key) ? base.filter((k) => k !== key) : [...base, key];
    });

  return (
    <div
      data-charge-section={status}
      className="flex flex-col"
      style={{
        gap: 6,
        padding: isDropTarget ? "8px 6px" : undefined,
        border: isDropTarget ? "2px dashed #6366F1" : "2px dashed transparent",
        borderRadius: 8,
        background: isDropTarget ? "#EEF0FF" : undefined,
        transition: "all 0.15s",
      }}
    >
      <span
        className="text-h4 font-medium"
        style={{ fontFamily: "Poppins", color: "#A1A1A1" }}
      >
        {title}
      </span>
      {groupByStatus
        ? groups.map((g) => (
            <ChargeGroup
              key={g.key}
              label={g.label}
              charges={g.charges}
              open={isOpen(g.key)}
              onToggle={() => toggle(g.key)}
              onSplitClick={onSplitClick}
              onChargeDragStart={onChargeDragStart}
              mergeTargetChargeId={mergeTargetChargeId}
            />
          ))
        : sorted.map((c) => (
            <ChargeCard
              key={c.id}
              charge={c}
              onSplitClick={onSplitClick}
              onDragStart={onChargeDragStart}
              isMergeTarget={mergeTargetChargeId === c.id}
            />
          ))}
      {charges.length === 0 && (
        <span
          className="text-h4 font-medium"
          style={{
            fontFamily: "Poppins",
            color: "#D9D9D9",
            fontStyle: "italic",
          }}
        >
          Aucune charge
        </span>
      )}
    </div>
  );
}

// --- Drop zone indicator for returning jobs to charges ---

export function ChargeDropZone({ active }: { active: boolean }) {
  return (
    <div
      className="flex items-center justify-center"
      style={{
        border: `2px dashed ${active ? "#6366F1" : "#D9D9D9"}`,
        borderRadius: 8,
        padding: "12px 8px",
        background: active ? "#EEF0FF" : "#FAFAFA",
        transition: "all 0.15s",
        marginTop: 8,
      }}
    >
      <span
        className="text-h4 font-medium"
        style={{
          fontFamily: "Poppins",
          color: active ? "#6366F1" : "#A1A1A1",
          textAlign: "center",
        }}
      >
        {active
          ? "Relâcher pour renvoyer en charge"
          : "Glisser un RDV ici pour le retirer"}
      </span>
    </div>
  );
}
